import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Baby,
  Banknote,
  CircleDot,
  Coins,
  Droplet,
  FileEdit,
  PackagePlus,
  ShoppingCart,
  Shuffle,
  Skull,
  TrendingDown,
  TrendingUp,
  XCircle,
} from "lucide-react";
import type { AnimalEvent, AnimalEventType } from "../types";
import { eventLabel } from "../utils/eventLabels";
import { formatEventPayload } from "../utils/eventPayloadFormatters";

type IconType = typeof CircleDot;

const ICON_BY_TYPE: Record<AnimalEventType, IconType> = {
  animal_registered: PackagePlus,
  animal_purchased: ShoppingCart,
  lot_cost_allocated: Coins,
  stage_transitioned: Shuffle,
  lot_changed: Shuffle,
  first_calving_recorded: Baby,
  calving_recorded: Baby,
  dry_off_recorded: Droplet,
  depreciation_accrued: TrendingDown,
  appreciation_accrued: TrendingUp,
  animal_died: Skull,
  animal_culled: XCircle,
  animal_sold: Banknote,
  event_corrected: FileEdit,
};

function iconFor(eventType: string): IconType {
  return ICON_BY_TYPE[eventType as AnimalEventType] ?? CircleDot;
}

function formatOccurred(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return format(d, "d MMM yyyy", { locale: es });
}

type Props = {
  event: AnimalEvent;
  isLast?: boolean;
};

export function AnimalTimelineEvent({ event, isLast = false }: Props) {
  const Icon = iconFor(event.eventType);

  return (
    <li className="relative pb-4 pl-12 last:pb-0">
      {!isLast && (
        <span aria-hidden className="absolute top-8 bottom-0 left-4 w-px bg-app-border" />
      )}
      <span
        aria-hidden
        className="absolute top-1 left-0 flex h-8 w-8 items-center justify-center rounded-full border border-app-border bg-app-surface-2"
      >
        <Icon className="h-4 w-4 text-app-ink-2" />
      </span>

      <article className="rounded-2xl border border-app-border bg-app-surface p-4">
        <header className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-medium text-[14px] text-app-ink-1">{eventLabel(event.eventType)}</h3>
          <time dateTime={event.occurredAt} className="text-[12px] text-app-ink-3 tabular-nums">
            {formatOccurred(event.occurredAt)}
          </time>
        </header>
        <p className="text-[13px] text-app-ink-2">
          {formatEventPayload(event.eventType, event.payload)}
        </p>
      </article>
    </li>
  );
}
